/**
 * sessionDivider — pure helper to build dictation-session dividers
 * (Feb 26, 2026)
 *
 * Called by useRecording when a transcription completes. Returns the new
 * value of the notes field after appending the transcribed text, with a
 * timestamped divider inserted if this transcription represents a NEW
 * dictation session (gap > GAP_MS from the previous one AND prev content
 * exists).
 *
 * Pure function — testable without React, without Deepgram, without timers.
 * The hook supplies `now` and `lastAt` from its own ref.
 *
 * Format: `\n\n--- ENCOUNTER NOTES (HH:MM) ---\n`
 */

const GAP_MS = 25 * 1000; // 25s — see useRecording.js for rationale
const MIN_PRIOR_LEN = 20; // don't divide if prev content is just a "test" stub

const fmtHHMM = (date) => {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
};

export const appendWithSessionDivider = ({
  prev,
  transcribedText,
  now = Date.now(),
  lastAt = 0,
  gapMs = GAP_MS,
  minPriorLen = MIN_PRIOR_LEN,
  separator = ' ',
}) => {
  if (!prev) return transcribedText;
  const trimmed = prev.trimEnd();
  const isNewSession = lastAt > 0 && (now - lastAt) > gapMs;
  if (isNewSession && trimmed.length > minPriorLen) {
    return `${trimmed}\n\n--- ENCOUNTER NOTES (${fmtHHMM(new Date(now))}) ---\n${transcribedText}`;
  }
  const needsNewline = trimmed.length > 0 && !trimmed.endsWith('\n');
  const sep = needsNewline ? separator : '';
  return trimmed + sep + transcribedText;
};
